import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, AlertCircle, CreditCard } from 'lucide-react';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const PaymentResult = () => {
  const { t, i18n } = useTranslation();
  const { getMe } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const recordResultSent = useRef(false);

  const successParam = searchParams.get('success');
  const success = successParam === 'true' || successParam === '1';

  useEffect(() => {
    if (successParam !== null && successParam !== undefined) {
      setStatus(success ? 'success' : 'failed');
    } else {
      setStatus('unknown');
    }
  }, [successParam, success]);

  useEffect(() => {
    const order = searchParams.get('order') || searchParams.get('order_id');
    if ((order || searchParams.get('id')) && !recordResultSent.current) {
      recordResultSent.current = true;
      const token = localStorage.getItem('token');
      if (token) {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const body = {
          order: order || searchParams.get('order'),
          order_id: searchParams.get('order_id'),
          id: searchParams.get('id'),
          success: searchParams.get('success'),
          error_occured: searchParams.get('error_occured'),
          amount_cents: searchParams.get('amount_cents'),
          currency: searchParams.get('currency'),
          data_message: searchParams.get('data.message'),
          source_data_type: searchParams.get('source_data.type'),
          source_data_pan: searchParams.get('source_data.pan'),
          source_data_sub_type: searchParams.get('source_data.sub_type'),
          acq_response_code: searchParams.get('acq_response_code'),
          txn_response_code: searchParams.get('txn_response_code'),
          merchant_order_id: searchParams.get('merchant_order_id'),
          created_at: searchParams.get('created_at'),
          is_3d_secure: searchParams.get('is_3d_secure'),
          is_auth: searchParams.get('is_auth'),
          is_capture: searchParams.get('is_capture')
        };
        fetch(`${API_BASE_URL}/payment/record-result`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        })
          .then((res) => { if (res.ok && successParam === 'true') getMe(); })
          .catch(() => {});
      }
    }
  }, [searchParams, successParam, getMe]);

  useEffect(() => {
    if (status === 'success') {
      getMe();
    }
  }, [status, getMe]);

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => navigate('/dashboard', { replace: true }), 4000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  const handleGoToDashboard = () => navigate('/dashboard', { replace: true });
  const handleTryAgain = () => navigate('/payment-required', { replace: true });

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="glass-card">
            <div className={`py-8 text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              {status === 'success' && (
                <>
                  <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                  <h1 className="text-3xl font-bold mb-4 text-green-500">
                    {t('auth.paymentSuccess') || 'Payment successful'}
                  </h1>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {t('auth.paymentSuccessMessage') || 'Your subscription is active. Redirecting to dashboard...'}
                  </p>
                  <button className="glass-button w-full py-3 text-lg font-semibold" onClick={handleGoToDashboard}>
                    {t('auth.goToDashboard')}
                  </button>
                </>
              )}
              {status === 'failed' && (
                <>
                  <AlertCircle className="h-20 w-20 mx-auto mb-6" style={{ color: 'var(--error-color)' }} />
                  <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--error-color)' }}>
                    {t('auth.paymentFailed') || 'Payment failed or cancelled'}
                  </h1>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {t('auth.paymentFailedMessage') || 'Your payment could not be completed. You can try again from the dashboard.'}
                  </p>
                  <button className="glass-button w-full py-3 text-lg font-semibold mb-3" onClick={handleTryAgain}>
                    {t('auth.tryAgain') || 'Try again'}
                  </button>
                  <button className="glass-button-outline w-full py-3 text-lg font-semibold" onClick={handleGoToDashboard}>
                    {t('auth.goToDashboard')}
                  </button>
                </>
              )}
              {status === 'unknown' && (
                <>
                  <CreditCard className="h-20 w-20 mx-auto mb-6" style={{ color: 'var(--accent-primary)' }} />
                  <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.paymentResult') || 'Payment result'}
                  </h1>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {t('auth.paymentResultMessage') || 'If you completed a payment, your subscription should be active. Go to the dashboard to continue.'}
                  </p>
                  <button className="glass-button w-full py-3 text-lg font-semibold" onClick={handleGoToDashboard}>
                    {t('auth.goToDashboard')}
                  </button>
                </>
              )}
              {status === null && (
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
                  <p style={{ color: 'var(--text-secondary)' }}>{t('auth.loading') || 'Loading...'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
